import useSWR from 'swr';

import myShopClient from '@services/nearlive';

/**
 * Billing status for the active shop — reads through to Stripe via
 * `GET /api/billing/status/{shopId}` (NEA-5710). Replaces the legacy
 * `useTrialStatus()` reads of `shop.trialStartedAt` / `paymentMethodId` /
 * `plan`. Source of truth: live Stripe catalogue (verified 2026-05-19) >
 * 14 May Engineering Brief.
 *
 * Returns the full superset shape from the endpoint:
 *   { status, plan, market, phase, isTrialing, trialStart, trialEnd,
 *     daysLeft, currentPeriodEnd, cancelAt, hasPaymentMethod, expired,
 *     cohortMonth, subscriptionId, stripeCustomerId, seats }
 *
 *   status: 'no_subscription' | 'trialing' | 'active' | 'past_due' |
 *           'canceled' | 'paused' | 'unpaid' | 'incomplete' | 'incomplete_expired'
 *
 * Direct `myShopClient` fetch (not via `dashboard-shared/services/billing`)
 * to avoid the `@nearst/ui` leak guard — mirrors `useOrders` / `useChannels`.
 *
 * The SWR key includes `shopId` so the shop-switcher naturally re-fetches.
 * For chains every shop resolves the same shared Stripe customer and therefore
 * the same status (correct: one payer per chain). SWR's stale-while-revalidate
 * covers the swap without blanking the dashboard. `revalidateOnFocus: false` —
 * billing rarely changes within a session, no need to thrash Stripe on focus.
 */
export function useBillingStatus(shopId) {
	const key = shopId ? ['billing-status', shopId] : null;
	const { data, error, isLoading, mutate } = useSWR(
		key,
		async () => {
			const client = await myShopClient();
			const { data } = await client.get(`/api/billing/status/${shopId}`);
			return data;
		},
		{ revalidateOnFocus: false }
	);

	return { billing: data ?? null, error, isLoading, mutate };
}
